import "./loot-rate-display-mode.js";
import {
  createBrowserLocalLootRateDisplayPreferenceProvider,
} from "./loot-rate-display-local-provider.js";

export const LOOT_RATE_DISPLAY_PREFERENCE_SCHEMA_VERSION = 1;

const MAX_DOCUMENT_CHARS = 1_024;
const DOCUMENT_FIELDS = Object.freeze(["mode", "revision", "schemaVersion"]);
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.loot-rate-display-preference-repository.v1",
);
const DISPLAY_MODE_CONTRACT = globalThis
  .__fishystuffLootRateDisplayModeContract;

if (
  !DISPLAY_MODE_CONTRACT
  || !Array.isArray(DISPLAY_MODE_CONTRACT.VALUES)
  || typeof DISPLAY_MODE_CONTRACT.parse !== "function"
) {
  throw new TypeError("Loot rate display mode contract is unavailable.");
}

export const LOOT_RATE_DISPLAY_MODES = DISPLAY_MODE_CONTRACT.VALUES;
export const DEFAULT_LOOT_RATE_DISPLAY_MODE =
  DISPLAY_MODE_CONTRACT.NORMALIZED;

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function exactFields(value, expected) {
  const fields = Object.keys(value).sort();
  return fields.length === expected.length
    && fields.every((field, index) => field === expected[index]);
}

function freezeState(value) {
  const next = { ...value };
  if (next.authoritative) next.authoritative = freezeState(next.authoritative);
  if (next.legacyCleanup) {
    next.legacyCleanup = Object.freeze({ ...next.legacyCleanup });
  }
  return Object.freeze(next);
}

function statesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function unavailable(operation, reason, authoritative = null) {
  return freezeState({
    kind: "unavailable",
    operation,
    reason,
    ...(authoritative ? { authoritative } : {}),
  });
}

function requireMode(value) {
  return DISPLAY_MODE_CONTRACT.parse(
    value,
    "Loot rate display preference mode",
  );
}

function isMode(value) {
  return LOOT_RATE_DISPLAY_MODES.includes(value);
}

function expectedRevisionFrom(options) {
  if (options === undefined) return undefined;
  if (
    !isRecord(options)
    || Object.keys(options).some((field) => field !== "expectedRevision")
  ) {
    throw new TypeError(
      "Loot-rate display command options contain unsupported fields.",
    );
  }
  const revision = options.expectedRevision;
  if (revision === undefined || revision === null) return revision;
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError(
      "Expected loot-rate display revision must be null or a positive safe integer.",
    );
  }
  return revision;
}

export function decodeLootRateDisplayPreferenceDocument(raw) {
  if (typeof raw !== "string") {
    return freezeState({
      kind: "invalid",
      reason: "stored-value-must-be-text",
      raw,
    });
  }
  if (raw.length === 0 || raw.length > MAX_DOCUMENT_CHARS) {
    return freezeState({
      kind: "invalid",
      reason: raw.length === 0
        ? "stored-value-must-not-be-empty"
        : "stored-value-exceeds-v1-limit",
      raw,
    });
  }
  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    return freezeState({ kind: "invalid", reason: "malformed-json", raw });
  }
  if (!isRecord(document)) {
    return freezeState({
      kind: "invalid",
      reason: "document-must-be-an-object",
      raw,
    });
  }
  if (
    Number.isSafeInteger(document.schemaVersion)
    && document.schemaVersion > LOOT_RATE_DISPLAY_PREFERENCE_SCHEMA_VERSION
  ) {
    return freezeState({
      kind: "unsupportedVersion",
      foundVersion: document.schemaVersion,
      raw,
    });
  }
  if (!exactFields(document, DOCUMENT_FIELDS)) {
    return freezeState({
      kind: "invalid",
      reason: "document-fields-must-match-v1",
      raw,
    });
  }
  if (document.schemaVersion !== LOOT_RATE_DISPLAY_PREFERENCE_SCHEMA_VERSION) {
    return freezeState({
      kind: "invalid",
      reason: "schema-version-must-be-1",
      raw,
    });
  }
  if (!Number.isSafeInteger(document.revision) || document.revision <= 0) {
    return freezeState({
      kind: "invalid",
      reason: "revision-must-be-positive",
      raw,
    });
  }
  if (!isMode(document.mode)) {
    return freezeState({
      kind: "invalid",
      reason: "mode-is-not-supported",
      raw,
    });
  }
  return freezeState({
    kind: "ready",
    revision: document.revision,
    mode: document.mode,
    raw,
  });
}

export function encodeLootRateDisplayPreferenceDocument({ revision, mode }) {
  if (!Number.isSafeInteger(revision) || revision <= 0) {
    throw new TypeError(
      "Loot-rate display revision must be a positive safe integer.",
    );
  }
  return JSON.stringify({
    schemaVersion: LOOT_RATE_DISPLAY_PREFERENCE_SCHEMA_VERSION,
    revision,
    mode: requireMode(mode),
  });
}

function authoritativeState(state) {
  return state?.authoritative ?? state;
}

export function lootRateDisplayPreferenceDecision(state) {
  const source = isRecord(state) ? state : { kind: "uninitialized" };
  const authoritative = authoritativeState(source);
  const ready = authoritative?.kind === "ready";
  return Object.freeze({
    repositoryKind: typeof source.kind === "string"
      ? source.kind
      : "uninitialized",
    revision: ready ? authoritative.revision : null,
    mode: ready ? authoritative.mode : DEFAULT_LOOT_RATE_DISPLAY_MODE,
    canChange: source.kind === "ready" || source.kind === "missing",
  });
}

export function createLootRateDisplayPreferenceRepository({
  provider = null,
} = {}) {
  if (
    !provider
    || typeof provider.dispose !== "function"
    || typeof provider.readDocument !== "function"
    || typeof provider.readRetiredMode !== "function"
    || typeof provider.removeDocument !== "function"
    || typeof provider.removeRetiredMode !== "function"
    || typeof provider.subscribeInvalidation !== "function"
    || typeof provider.writeDocument !== "function"
  ) {
    throw new TypeError(
      "Loot-rate display repository requires its exact local provider.",
    );
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function decision() {
    return lootRateDisplayPreferenceDecision(currentState);
  }

  function deliver(listener) {
    try {
      listener(state());
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(nextState) {
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return state();
    currentState = frozen;
    if (!disposed) {
      for (const listener of listeners) deliver(listener);
    }
    return state();
  }

  function disposedResult(operation) {
    return unavailable(
      operation,
      "loot-rate-display-repository-disposed",
      authoritativeState(currentState),
    );
  }

  function readAuthoritativeState() {
    const stored = provider.readDocument();
    if (stored.kind === "unavailable") {
      return unavailable(stored.operation, stored.reason);
    }
    return stored.raw === null
      ? freezeState({ kind: "missing", raw: null })
      : decodeLootRateDisplayPreferenceDocument(stored.raw);
  }

  function withRetiredCleanup(authoritative) {
    const cleanup = provider.removeRetiredMode();
    if (cleanup.kind !== "unavailable") return authoritative;
    return freezeState({
      ...authoritative,
      legacyCleanup: {
        kind: "unavailable",
        operation: cleanup.operation,
        reason: cleanup.reason,
      },
    });
  }

  function conflict(expectedRevision, authoritative) {
    const exactAuthoritative = authoritativeState(authoritative);
    return publish({
      kind: "conflict",
      expectedRevision,
      actualRevision: exactAuthoritative?.kind === "ready"
        ? exactAuthoritative.revision
        : null,
      authoritative: exactAuthoritative,
    });
  }

  function writeReady({
    revision,
    mode,
    previousRaw,
    expectedRevision,
    authoritative,
  }) {
    const beforeWrite = provider.readDocument();
    if (beforeWrite.kind === "unavailable") {
      return publish(unavailable(
        beforeWrite.operation,
        beforeWrite.reason,
        authoritative,
      ));
    }
    if (beforeWrite.raw !== previousRaw) {
      return conflict(expectedRevision, readAuthoritativeState());
    }

    const raw = encodeLootRateDisplayPreferenceDocument({ revision, mode });
    const written = provider.writeDocument(raw);
    if (written.kind === "unavailable") {
      return publish(unavailable(
        written.operation,
        written.reason,
        authoritative,
      ));
    }

    const readBack = provider.readDocument();
    if (readBack.kind === "unavailable") {
      return publish(unavailable(
        readBack.operation,
        readBack.reason,
        authoritative,
      ));
    }
    if (readBack.raw !== raw) {
      return conflict(expectedRevision, readAuthoritativeState());
    }
    return publish(withRetiredCleanup(
      decodeLootRateDisplayPreferenceDocument(readBack.raw),
    ));
  }

  function load() {
    if (disposed) return state();
    publish({ kind: "loading" });
    const authoritative = readAuthoritativeState();
    if (authoritative.kind === "ready") {
      return publish(withRetiredCleanup(authoritative));
    }
    if (authoritative.kind !== "missing") return publish(authoritative);

    const retired = provider.readRetiredMode();
    if (retired.kind === "unavailable") {
      return publish(unavailable(retired.operation, retired.reason));
    }
    if (retired.kind === "missing") return publish(authoritative);
    if (retired.kind === "invalid") {
      return publish({
        kind: "invalid",
        reason: retired.reason,
        raw: retired.raw,
        source: "retired-ui-settings",
      });
    }
    if (!isMode(retired.value)) {
      return publish({
        kind: "invalid",
        reason: "retired-mode-is-not-supported",
        raw: retired.raw,
        source: "retired-ui-settings",
      });
    }
    return writeReady({
      revision: 1,
      mode: retired.value,
      previousRaw: null,
      expectedRevision: null,
      authoritative,
    });
  }

  async function setMode(mode, options = {}) {
    const exactMode = requireMode(mode);
    const expectedRevision = expectedRevisionFrom(options);
    if (disposed) return disposedResult("write");

    let authoritative = readAuthoritativeState();
    if (authoritative.kind === "missing") {
      const loaded = load();
      if (!["missing", "ready"].includes(loaded.kind)) return loaded;
      authoritative = readAuthoritativeState();
    }
    if (!["missing", "ready"].includes(authoritative.kind)) {
      return publish(authoritative);
    }

    const actualRevision = authoritative.kind === "ready"
      ? authoritative.revision
      : null;
    if (
      expectedRevision !== undefined
      && expectedRevision !== actualRevision
    ) {
      if (authoritative.kind === "ready" && authoritative.mode === exactMode) {
        return publish(withRetiredCleanup(authoritative));
      }
      return conflict(expectedRevision, authoritative);
    }
    if (authoritative.kind === "ready" && authoritative.mode === exactMode) {
      return publish(withRetiredCleanup(authoritative));
    }
    if (
      authoritative.kind === "ready"
      && authoritative.revision >= Number.MAX_SAFE_INTEGER
    ) {
      return publish(unavailable(
        "write",
        "revision-exhausted",
        authoritative,
      ));
    }

    publish({
      kind: "pending",
      operation: "setMode",
      requestedMode: exactMode,
      baseRevision: actualRevision,
      authoritative,
    });
    return writeReady({
      revision: actualRevision === null ? 1 : actualRevision + 1,
      mode: exactMode,
      previousRaw: authoritative.raw,
      expectedRevision: expectedRevision === undefined
        ? actualRevision
        : expectedRevision,
      authoritative,
    });
  }

  async function exportRecord() {
    if (disposed) return disposedResult("export");
    const stored = provider.readDocument();
    if (stored.kind === "unavailable") {
      return unavailable("export", stored.reason);
    }
    const records = [];
    if (stored.raw !== null) {
      const decoded = decodeLootRateDisplayPreferenceDocument(stored.raw);
      records.push(Object.freeze({
        source: "canonical",
        recordKind: decoded.kind,
        raw: stored.raw,
      }));
    }
    const retired = provider.readRetiredMode();
    if (retired.kind === "unavailable") {
      return unavailable("export", retired.reason);
    }
    if (retired.kind !== "missing") {
      records.push(Object.freeze({
        source: "retired-ui-settings",
        recordKind: retired.kind === "candidate" && isMode(retired.value)
          ? "retired"
          : "retiredInvalid",
        raw: retired.raw,
      }));
    }
    return records.length === 0
      ? freezeState({ kind: "missing" })
      : Object.freeze({
        kind: "exported",
        records: Object.freeze(records),
      });
  }

  async function reset(options = {}) {
    const expectedRevision = expectedRevisionFrom(options);
    if (disposed) return disposedResult("remove");
    const canonicalBeforeReset = provider.readDocument();
    if (canonicalBeforeReset.kind === "unavailable") {
      return publish(unavailable(
        canonicalBeforeReset.operation,
        canonicalBeforeReset.reason,
      ));
    }
    const canonicalRaw = canonicalBeforeReset.raw;
    const authoritative = canonicalRaw === null
      ? freezeState({ kind: "missing", raw: null })
      : decodeLootRateDisplayPreferenceDocument(canonicalRaw);

    const actualRevision = authoritative.kind === "ready"
      ? authoritative.revision
      : null;
    if (
      expectedRevision !== undefined
      && !["missing", "ready"].includes(authoritative.kind)
    ) {
      return publish(authoritative);
    }
    if (
      expectedRevision !== undefined
      && expectedRevision !== actualRevision
    ) {
      return conflict(expectedRevision, authoritative);
    }

    publish({
      kind: "pending",
      operation: "reset",
      baseRevision: actualRevision,
      authoritative,
    });
    const retired = provider.removeRetiredMode();
    if (retired.kind === "unavailable") {
      return publish(unavailable(
        retired.operation,
        retired.reason,
        authoritative,
      ));
    }

    const beforeRemove = provider.readDocument();
    if (beforeRemove.kind === "unavailable") {
      return publish(unavailable(
        beforeRemove.operation,
        beforeRemove.reason,
        authoritative,
      ));
    }
    if (beforeRemove.raw !== canonicalRaw) {
      return conflict(
        expectedRevision ?? actualRevision,
        readAuthoritativeState(),
      );
    }
    const removed = provider.removeDocument();
    if (removed.kind === "unavailable") {
      return publish(unavailable(
        removed.operation,
        removed.reason,
        authoritative,
      ));
    }
    return publish({ kind: "missing", raw: null });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError(
        "Loot-rate display repository listener must be a function.",
      );
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  const unsubscribeInvalidation = provider.subscribeInvalidation(() => load());

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribeInvalidation();
    listeners.clear();
    provider.dispose();
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: LOOT_RATE_DISPLAY_PREFERENCE_SCHEMA_VERSION,
    decision,
    dispose,
    exportRecord,
    load,
    reset,
    setMode,
    state,
    subscribe,
  });
}

export function createBrowserLootRateDisplayPreferenceRepository() {
  return createLootRateDisplayPreferenceRepository({
    provider: createBrowserLocalLootRateDisplayPreferenceProvider(),
  });
}
